/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Tariffs And Services Side Search Component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.10.0
 * @since 1.4.0
 */

import { ChangeEvent, Fragment, useCallback, useEffect, useState } from 'react';

import slice from 'lodash/slice';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../assets';
import { Input, LinkBlock } from '../../../components/common';
import { makeSelectTranslationPageProducts } from '../../../modules/internationalization/intSelectors';
import { IPageProductsTranslation } from '../../../modules/internationalization/types/pages/products';
import { IRootReducer } from '../../../reducers/rootReducer';
import {
    tariffsAndServicesSearchStartAction,
    tariffsAndServicesSetSearchValueAction,
} from '../tariffsAndServicesActions';
import { TProductItem } from '../tariffsAndServicesProductTypes';
import {
    makeSelectProductsSearchValue,
    makeSelectTariffsAndServicesSearchedItems,
} from '../tariffsAndServicesSelectors';
import {
    TariffsAndServicesSideSearchFoundItemStyled,
    TariffsAndServicesSideSearchFoundStyled,
    TariffsAndServicesSideSearchStyled,
} from './styled/TariffsAndServicesSideSearchStyled';

export type TTariffsAndServicesSideSearch = IConnectedProps & IDispatchProps;

export const ProductsSideSearch = (props: TTariffsAndServicesSideSearch) => {
    const [showSearchedValues, setShowSearchedValues] = useState(false);
    const [searchedItems, setSearchedItems] = useState<TProductItem[]>([]);
    const [showMoore, setShowMoore] = useState(false);

    /**
     * Max searched items - 10
     */
    useEffect(() => {
        setSearchedItems(slice(props.searchedItems, 0, 10));
        setShowMoore(props.searchedItems.length > 10);
    }, [props.searchedItems])

    const onChangeHandler = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setShowSearchedValues(true);
        setSearchValue(value);
    }, []);

    const onClearHandler = useCallback(() => {
        setSearchValue('');
        startSearch();
    }, []);

    const setSearchValue = useCallback((value: string) => {
        props.setSearchValue(value);
    }, [props.setSearchValue]);

    const startSearch = useCallback(() => {
        setShowSearchedValues(false);
        props.searchStart();
    }, []);

    return <Fragment>
        <TariffsAndServicesSideSearchStyled>
            <Input
                placeholder={props.translation.search.inputPlaceholder}
                righticon={props.searchValue ? icons.cancelIcon : icons.searchMediumIcon}
                righticonhandler={onClearHandler}
                value={props.searchValue}
                onChange={onChangeHandler}
                onEnter={startSearch}
            />
            {
                showSearchedValues &&
                <TariffsAndServicesSideSearchFoundStyled>
                    {
                        searchedItems.map(item => <Fragment key={item.id}>
                            <TariffsAndServicesSideSearchFoundItemStyled
                                onClick={() => {
                                    setSearchValue(item.title);
                                    startSearch();
                                }}>
                                {item.title}
                            </TariffsAndServicesSideSearchFoundItemStyled>
                        </Fragment>)
                    }
                    {
                        showMoore && <TariffsAndServicesSideSearchFoundItemStyled
                            data-name="showMooreItem"
                            onClick={startSearch}
                        >
                            <LinkBlock>{ props.translation.search.showMoore }</LinkBlock>
                        </TariffsAndServicesSideSearchFoundItemStyled>
                    }
                </TariffsAndServicesSideSearchFoundStyled>
            }
        </TariffsAndServicesSideSearchStyled>
    </Fragment>
}

interface IConnectedProps {
    searchValue: string;
    searchedItems: TProductItem[];
    translation: IPageProductsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    searchValue: makeSelectProductsSearchValue(),
    searchedItems: makeSelectTariffsAndServicesSearchedItems(),
    translation: makeSelectTranslationPageProducts(),
});

interface IDispatchProps {
    searchStart: () => void;
    setSearchValue: (payload: string) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    searchStart: () => dispatch(tariffsAndServicesSearchStartAction()),
    setSearchValue: (payload) => dispatch(tariffsAndServicesSetSearchValueAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductsSideSearch);
